class HistoryService {
  constructor() {
    this.conversations = [];
    this.dailyStats = new Map();
    this.loadFromStorage();
  }

  static getInstance() {
    if (!HistoryService.instance) {
      HistoryService.instance = new HistoryService();
    }
    return HistoryService.instance;
  }

  // Initialise une nouvelle session de conversation
  startConversationSession(childId, childName) {
    const sessionId = `session_${Date.now()}_${childId}`;
    
    const session = {
      id: sessionId,
      childId,
      childName,
      startTime: new Date(),
      messages: [],
      totalMessages: 0,
      avgComplexity: 0,
    };

    this.conversations.push(session);
    console.log(`🗣️ Nouvelle session pour ${childName}: ${sessionId}`);
    
    return sessionId;
  }

  // Termine une session de conversation
  async endConversationSession(sessionId) {
    const session = this.conversations.find(s => s.id === sessionId);
    if (session) {
      session.endTime = new Date();
      session.totalMessages = session.messages.length;
      session.avgComplexity = this.calculateAvgComplexity(session.messages);
      
      await this.updateDailyStats(session);
      await this.saveToStorage();
      
      console.log(`✅ Session terminée ${sessionId}: ${session.totalMessages} messages`);
    }
  }

  // Ajoute un message à l'historique
  async addMessage(sessionId, question, answer, complexityLevel, category = 'general') {
    const session = this.conversations.find(s => s.id === sessionId);
    if (!session) {
      // Crée une session si elle n'existe pas
      const newSession = {
        id: sessionId,
        childId: sessionId.split('_')[2] || 'unknown',
        childName: 'Enfant',
        startTime: new Date(),
        messages: [],
        totalMessages: 0,
        avgComplexity: 0,
      };
      this.conversations.push(newSession);
    }

    const targetSession = this.conversations.find(s => s.id === sessionId);
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const message = {
      id: messageId,
      question,
      answer,
      childId: targetSession.childId,
      childName: targetSession.childName,
      timestamp: new Date(),
      complexityLevel,
      category,
      favorite: false,
    };

    targetSession.messages.push(message);
    
    // Limite à 50 messages par session pour les performances
    if (targetSession.messages.length > 50) {
      targetSession.messages = targetSession.messages.slice(-50);
    }

    await this.saveToStorage();
    console.log(`💬 Message ajouté à ${sessionId}: ${question.substring(0, 30)}...`);
    
    return messageId;
  }

  // Met à jour le feedback d'un message
  async updateMessageFeedback(messageId, feedback) {
    for (const session of this.conversations) {
      const message = session.messages.find(m => m.id === messageId);
      if (message) {
        message.feedback = feedback;
        await this.saveToStorage();
        console.log(`👍 Feedback ajouté au message ${messageId}: ${feedback}`);
        return;
      }
    }
  }

  // Marque/démarque un message comme favori
  async toggleFavorite(messageId) {
    for (const session of this.conversations) {
      const message = session.messages.find(m => m.id === messageId);
      if (message) {
        message.favorite = !message.favorite;
        await this.saveToStorage();
        console.log(`⭐ Message ${messageId} ${message.favorite ? 'ajouté aux' : 'retiré des'} favoris`);
        return message.favorite;
      }
    }
    return false;
  }

  // Récupère l'historique d'un enfant
  getChildHistory(childId, limit = 20) {
    const childMessages = [];
    
    for (const session of this.conversations) {
      if (session.childId === childId || childId === 'all') {
        childMessages.push(...session.messages.map(msg => ({
          ...msg,
          sessionId: session.id
        })));
      }
    }

    // Trie par date décroissante et limite
    return childMessages
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  }

  // Récupère les favoris d'un enfant
  getChildFavorites(childId) {
    const childMessages = this.getChildHistory(childId, 1000);
    return childMessages.filter(m => m.favorite);
  }

  // Recherche dans l'historique
  searchHistory(query, childId, category) {
    const allMessages = [];
    
    for (const session of this.conversations) {
      if (!childId || childId === 'all' || session.childId === childId) {
        allMessages.push(...session.messages);
      }
    }

    const queryLower = query.toLowerCase();
    
    return allMessages.filter(message => {
      const matchesQuery = 
        message.question.toLowerCase().includes(queryLower) ||
        message.answer.toLowerCase().includes(queryLower);
      
      const matchesCategory = !category || message.category === category;
      
      return matchesQuery && matchesCategory;
    });
  }

  // Statistiques d'un enfant
  getChildStats(childId) {
    const childMessages = this.getChildHistory(childId, 1000);
    const today = new Date().toDateString();
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    const questionsToday = childMessages.filter(m => 
      new Date(m.timestamp).toDateString() === today
    ).length;

    const questionsThisWeek = childMessages.filter(m => 
      new Date(m.timestamp) > weekAgo
    ).length;

    const avgComplexity = this.calculateAvgComplexity(childMessages);
    
    // Calcule les catégories favorites
    const categoryCount = {};
    childMessages.forEach(m => {
      if (m.category) {
        categoryCount[m.category] = (categoryCount[m.category] || 0) + 1;
      }
    });

    const favoriteCategories = Object.entries(categoryCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category);

    // Calcule le progrès d'apprentissage (basé sur l'évolution du feedback)
    const recentMessages = childMessages.slice(0, 10);
    const understoodRatio = recentMessages.length > 0 ? 
      recentMessages.filter(m => m.feedback === 'understood').length / recentMessages.length : 0;
    const learningProgress = Math.round(understoodRatio * 100);

    return {
      totalQuestions: childMessages.length,
      questionsToday,
      questionsThisWeek,
      avgComplexity,
      favoriteCategories,
      learningProgress,
    };
  }

  // Exporte l'historique pour partage
  async exportHistory(childId, format = 'json') {
    const childMessages = this.getChildHistory(childId, 1000);
    const childName = childMessages[0]?.childName || 'Enfant';

    if (format === 'json') {
      return JSON.stringify({
        child: { id: childId, name: childName },
        exportDate: new Date().toISOString(),
        totalMessages: childMessages.length,
        messages: childMessages,
      }, null, 2);
    } else {
      let text = `Historique de ${childName}\n`;
      text += `Exporté le ${new Date().toLocaleDateString()}\n`;
      text += `Total: ${childMessages.length} conversations\n\n`;

      childMessages.forEach((message, index) => {
        text += `--- Conversation ${index + 1} ---\n`;
        text += `Date: ${new Date(message.timestamp).toLocaleDateString()}\n`;
        text += `Question: ${message.question}\n`;
        text += `Réponse: ${message.answer}\n`;
        if (message.feedback) {
          text += `Feedback: ${message.feedback}\n`;
        }
        text += '\n';
      });

      return text;
    }
  }

  // Nettoie l'ancien historique
  async cleanOldHistory(daysToKeep = 90) {
    const cutoffDate = new Date(Date.now() - daysToKeep * 24 * 60 * 60 * 1000);
    let removedCount = 0;

    this.conversations = this.conversations.filter(session => {
      if (new Date(session.startTime) < cutoffDate) {
        removedCount += session.messages.length;
        return false;
      }
      return true;
    });

    await this.saveToStorage();
    console.log(`🧹 Nettoyage historique: ${removedCount} messages supprimés`);
    
    return removedCount;
  }

  // Méthodes privées
  calculateAvgComplexity(messages) {
    if (messages.length === 0) return 0;
    const sum = messages.reduce((acc, msg) => acc + (msg.complexityLevel || 0), 0);
    return Number((sum / messages.length).toFixed(1));
  }

  async updateDailyStats(session) {
    const dateKey = new Date(session.startTime).toDateString();
    let stats = this.dailyStats.get(dateKey);

    if (!stats) {
      stats = {
        date: dateKey,
        totalQuestions: 0,
        childrenActive: [],
        avgComplexity: 0,
        favoriteTopics: [],
      };
    }

    stats.totalQuestions += session.messages.length;
    if (!stats.childrenActive.includes(session.childId)) {
      stats.childrenActive.push(session.childId);
    }

    stats.avgComplexity = this.calculateAvgComplexity(session.messages);
    this.dailyStats.set(dateKey, stats);
  }

  async saveToStorage() {
    try {
      localStorage.setItem('conversation_history', JSON.stringify(this.conversations));
      localStorage.setItem('daily_stats', JSON.stringify(Array.from(this.dailyStats.entries())));
      console.log('💾 Historique sauvegardé localement');
    } catch (error) {
      console.error('Erreur sauvegarde historique:', error);
    }
  }

  loadFromStorage() {
    try {
      const historyData = localStorage.getItem('conversation_history');
      const statsData = localStorage.getItem('daily_stats');

      if (historyData) {
        this.conversations = JSON.parse(historyData).map(session => ({
          ...session,
          startTime: new Date(session.startTime),
          endTime: session.endTime ? new Date(session.endTime) : null,
          messages: session.messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
      }

      if (statsData) {
        this.dailyStats = new Map(JSON.parse(statsData));
      }

      console.log(`📚 Historique chargé: ${this.conversations.length} sessions`);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  }
}

export default HistoryService;